import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { validateForm } from "../../../utils/validationUtils";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

export const Diabetes = ({ individualId, diabScreenId, viewMode, selectedUser, setUserError }) => {
    const { screeningData, setLoading, setScreeningData, setError, clearScreeningData } = useNCD();
    const today = new Date().toISOString().split("T")[0];
    const [formErrors, setFormErrors] = useState({});
    const diabscreen = screeningData[`diabetes_${individualId}`];

    const [diabscreenform, setDiabScreenform] = useState({
        date: "",
        rbs: "",
        hba1c: "",
        referred: "",
        referredTo: "",
        remarks: "",
        underTreatment: true,
        markKnownCaseFlag: false,
        referredToIfOther: null,
        examination: null,
        referral: null,
        currentScreeningId: null,
        screeningType: "FirstTime Screening"
    });

    useEffect(() => {

        const fetchDiabetes = async () => {
            try {
                if (!individualId) return;

                setLoading(true);
                let diabData;

                if (diabScreenId) {
                    diabData = await ncdAPI.getScreeningData(600, individualId, diabScreenId);
                } else {
                    const screeningresult = await ncdAPI.getScreenDetails(individualId);
                    diabData = await ncdAPI.getScreeningData(600, individualId, screeningresult?.data[600]?.screeningId);
                }

                setScreeningData(`diabetes_${individualId}`, diabData?.data);

                setLoading(false);
            } catch (error) {
                setError("Error fetching diabetes data");
                setLoading(false);
            }
        };

        if (viewMode === 'VIEW' && !diabscreen) {
            fetchDiabetes();
        }

    }, [individualId, diabScreenId, viewMode]);

    useEffect(() => {
        if (diabscreen?.screenDateDisable && diabscreen?.date) {
            setDiabScreenform(prev => ({
                ...prev,
                date: formatToInputDate(diabscreen.date)
            }));
        } else {
            setDiabScreenform(prev => ({
                ...prev,
                date: today
            }));
        }
    }, [diabscreen]);


    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "date") {
            const selectedDate = new Date(value);
            const formattedDate = selectedDate
                .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                })
                .replace(/\//g, "-");

            setDiabScreenform(prev => ({
                ...prev,
                [name]: formattedDate
            }));
        } else {
            const newValue = value === "yes" ? true : value === "no" ? false : value;

            setDiabScreenform(prev => {
                const updatedForm = {
                    ...prev,
                    [name]: newValue,
                };

                // If "referred" is changed to "no", reset referredTo
                if (name === "referred" && value === "no") {
                    updatedForm.referredTo = "";
                }

                // If "referredTo" is changed from "Others" to something else, clear referredToIfOther
                if (name === "referredTo" && value !== "Others") {
                    updatedForm.referredToIfOther = null;
                }

                return updatedForm;
            });
        }

        setFormErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    const dbFormSchema = {
        date: { required: true, message: "This field is required." },
        rbs: {
            required: true,
            type: "number",
            message: "This field is required.",
        },
        // hba1c: {
        //     required: true,
        //     type: "number",
        //     message: "This field is required.",
        // },
        referred: {
            customValidator: (value) => {
                if (value !== true && value !== false) {
                    return "Please select an option for referral.";
                }
                return null;
            },
        },
        referredTo: {
            required: (diabscreenform) => {
                return diabscreenform.referred === true; // Only required if referred is true
            },
            message: "Please select where to refer.",
        },
        referredToIfOther: {
            required: (diabscreenform) => {
                return (
                    diabscreenform.referred === true &&
                    diabscreenform.referredTo === "Others"
                );
            },
            message: "Please specify other referral."
        }
        //  remarks: { required: true, message: "Remark is required." },
    };

    const handleSubmit = async (e) => {

        e.preventDefault();

        const errors = validateForm(diabscreenform, dbFormSchema);
        // If there are errors, update state and do not submit
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (!selectedUser) {
            setUserError(true);
            toast.error("Please select SHC staff", { className: "custom-toast-error" });
            return false;
        }

        try {
            setLoading(true);

            // ✅ Inject selectedUser at submission time 

            const payload = {
                ...diabscreenform,
                date: formatToApiDate(diabscreenform.date),
                currentScreeningId: diabScreenId || diabscreen?.currentScreeningId,
                createdUser: selectedUser,
                screeningANM: {
                    users: selectedUser,
                    anmValue: selectedUser,
                    viaTrained: true
                }
            };

            const response = await ncdAPI.submitDb(600, individualId, payload);

            if (response.success) {
                // clear data after successful submit
                clearScreeningData(`diabetes_${individualId}`);
                toast.success(`Diabetes data saved successfully`);
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }
    };

    return (
        <>

            {viewMode === "ADD" ? (

                <>
                    <form onSubmit={(e) => handleSubmit(e)}>
                        <div className=" d-flex  align-items-center mb-4">
                            <span className="exam-label ml-5">Mark as a known case <span className="text-danger">*</span></span>
                            <div className="d-flex align-items-center" style={{ marginLeft: "9%" }}>
                                <label className="d-flex align-items-center gap-1">
                                    <input
                                        type="radio"
                                        name="underTreatment"
                                        value="yes"
                                        checked={diabscreenform.underTreatment === true}
                                        onChange={handleChange}
                                        className="form-check-input"
                                    /> Yes
                                </label>
                                <label className="d-flex align-items-center gap-1">
                                    <input
                                        type="radio"
                                        name="underTreatment"
                                        value="no"
                                        checked={diabscreenform.underTreatment === false}
                                        onChange={handleChange}
                                        className="ml-20 form-check-input"
                                    /> No
                                </label>
                            </div>
                        </div>
                        <div className="personal-exam-row">
                            <div className="exam-item">
                                <span className="exam-label">Date <span className="text-danger">*</span></span>
                                <input
                                    type="date"
                                    name="date"
                                    className="exam-textarea"
                                    value={diabscreenform.date}
                                    onChange={handleChange}
                                    max={today}
                                    disabled={diabscreen?.screenDateDisable}
                                />

                                {formErrors.date && <p className="error">{formErrors.date}</p>}
                            </div>

                            <div className="exam-item">
                                <span className="exam-label">Random Blood Sugar (mg/dl) <span className="text-danger">*</span></span>
                                <input type="text" className="exam-textarea" name="rbs" maxLength={3} onChange={handleChange}></input>
                                {formErrors.rbs && <p className="error">{formErrors.rbs}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">HbA1c (%)</span>
                                <input type="text" className="exam-textarea" name="hba1c" maxLength={3} onChange={handleChange}></input>
                                {formErrors.hba1c && <p className="error">{formErrors.hba1c}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">Refer </span>
                                <div>
                                    {diabscreenform.underTreatment === true ? (
                                        // Only Yes radio, selected by default
                                        <label className="d-flex align-items-center gap-1">
                                            <input
                                                type="radio"
                                                name="referred"
                                                value="yes"
                                                className="exam_radio form-check-input"
                                                checked={diabscreenform.referred === true}
                                                onChange={handleChange}
                                            /> Yes
                                        </label>
                                    ) : (
                                        // Both Yes and No radios
                                        <>
                                            <label className="align-items-center">
                                                <input
                                                    type="radio"
                                                    name="referred"
                                                    value="yes"
                                                    className="exam_radio form-check-input"
                                                    checked={diabscreenform.referred === true}
                                                    onChange={handleChange}
                                                /> Yes
                                            </label>
                                            <label className="align-items-center">
                                                <input
                                                    type="radio"
                                                    name="referred"
                                                    value="no"
                                                    className="exam_radio ml-20 form-check-input"
                                                    checked={diabscreenform.referred === false}
                                                    onChange={handleChange}
                                                /> No
                                            </label>
                                        </>
                                    )}
                                </div>
                                {formErrors.referred && <p className="error">{formErrors.referred}</p>}
                            </div>

                            <div className="exam-item">
                                <span className="exam-label">Refer To </span>
                                <select
                                    className="exam-dropdown"
                                    name="referredTo"
                                    onChange={handleChange}
                                    disabled={diabscreenform.referred !== true}
                                    value={diabscreenform.referredTo || ""}
                                >
                                    <option value="">Select</option>
                                    <option value="PHC">PHC</option>
                                    <option value="CHC">CHC</option>
                                    <option value="DH">District Hospital</option>
                                    <option value="Others">Other</option>
                                </select>
                                {formErrors.referredTo && <p className="error">{formErrors.referredTo}</p>}
                            </div>

                            <div className="exam-item">
                                <span className="exam-label">If Other, Specify {diabscreenform.referredTo === "Others" && <span className="text-danger">*</span>}</span>
                                <input
                                    className="exam-textarea"
                                    name="referredToIfOther"
                                    value={diabscreenform.referredToIfOther || ""}
                                    onChange={handleChange}
                                    disabled={diabscreenform.referredTo !== "Others"}
                                />
                                {formErrors.referredToIfOther && diabscreenform.referredTo === "Others" && (
                                    <p className="error">{formErrors.referredToIfOther}</p>
                                )}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">Remarks </span>
                                <input type="text" className="exam-textarea" name="remarks" onChange={handleChange}></input>
                                {formErrors.remarks && <p className="error">{formErrors.remarks}</p>}
                            </div>

                        </div>

                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save</button>
                        </div>

                    </form>

                </>

            ) : (

                <div className="htn-screen table-responsive">

                    <table className="table table-responsive custom-striped">
                        <thead className="table-head">
                            <tr>
                                <th>Date</th>
                                <th>Random Blood Sugar (mg/dl)</th>
                                <th>HbA1c (%)</th>
                                <th>Refer</th>
                                <th>Refer To</th>
                                <th>If Other, Specify</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {diabscreen?.rbs ? (
                                <tr>
                                    <td>{diabscreen?.date ?? '--'}</td>
                                    <td>{diabscreen?.rbs ?? '--'}</td>
                                    <td>{diabscreen?.hba1c ?? '--'}</td>
                                    <td>{diabscreen?.referred ? 'Yes' : 'No'}</td>
                                    <td>{diabscreen?.referredTo ?? '--'}</td>
                                    <td>{diabscreen?.other ?? '--'}</td>
                                    <td>{diabscreen?.remarks ?? '--'}</td>
                                </tr>
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>No record found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            )}
        </>
    );
};
